<template>

  <div>

    <approval-remarks :row-data="actionData" :steps="actionData.steps" :legal-step="3" />

    <section v-if="isBackhaul">

        <!-- XOL-552: "+ Backhaul" sits above the site's line table so a Tower Co
             user can add the missing line without leaving the RTB. It opens the
             full BackhaulProvider form in new mode, stamped with this RTB, with
             Provider = the telco's Backhaul twin and Type = the RTB's type.
             `sharer-id` is deliberately NOT passed: it would flip the popup to
             "Customer". `:table-ref="null"` stops the stray
             backhaultable-refresh-table broadcast. The new line is not
             auto-selected — the list refreshes and the user picks. -->
        <template v-if="isTowerCo() && actionData.subActionType === 'CUST'">
          <!-- XOL-596 (completed steps, CS-B): "+ Backhaul" creates a line, so it is not
               rendered while step 3 is complete (a disabled button's tooltip can't be reached). -->
          <div v-if="!frozen" class="text-right">
            <backhaul-action
              :provider="true"
              button-text="Backhaul"
              :site-id="actionData.site_id"
              :site-name="site ? site.siteName : null"
              :action-id="actionData.id"
              :default-provider-id="telcoBackhaulTwinId"
              :default-backhaul-type="actionData.backhaulType"
              :action-step-id="step3ActionStepId"
              :read-only="readOnly"
              :table-ref="null"
              close-on-save
              @saved="onBackhaulLineAdded" />
          </div>

          <backhaul-customer
            ref="bhCustomer"
            :sharer-id="actionData.organisation_id"
            :site-id="actionData.site_id"
            :action-id="actionData.id"
            :site-code="site ? site.siteId : null"
            :site-name="site ? site.siteName : null"
            :read-only="readOnly"
            :step-frozen="frozen"
            :action-step-id="step3ActionStepId"
            :is-request="true" />
        </template>

        <!-- XOL-526 (C4, UX §2.2): a Telco is no longer mounted here at all —
             the backhaul details are the Tower Co's and the provider's, not
             the telco's. `:editable` and the mount condition are the SAME
             expression: a Tower Co or the provider can land here; nobody
             else does. lockedForViewer inside BackhaulProvider still holds
             the actual editing controls to Tower-Co-only. -->
        <backhaul-provider
          v-else-if="isTowerCo() || isRequestProvider()"
          :provider-id="actionData.provider_id"
          :site-id="actionData.site_id"
          :action-id="actionData.id"
          :editable="isTowerCo() || isRequestProvider()"
          :step-frozen="frozen"
          :action-step-id="step3ActionStepId"
          :is-request="true" />

    </section>

    <section v-if="isTowerCo() && canAccess('finance')">

        <el-form :inline="true">
            <el-form-item label="Agreement Type">

              <el-select
                v-model="step3.docType"
                placeholder="Select"
                filterable
                allow-create
                :disabled="!canAccess('finance') || disabled || frozen || locked"
                :aria-describedby="(locked && !disabled) ? 'step3-approval-lock-line' : null"
                @change="docTypeChanged">

                <el-option
                   v-for="item in types"
                   :key="item.value"
                   :label="item.label"
                   :value="item.value">
                 </el-option>

              </el-select>
              <field-cue :row="actionData" path="state.step3.docType" label="Agreement Type" layout="inline" />

          </el-form-item>
        </el-form>


      <p>Agreement Type: <b>{{step3.docType}}</b></p>

      <el-collapse v-model="accordion" v-if="!disabled" accordion>

      <!-- XOL-596 (Carlos, 2026-10-14: "Move the lock to the top of the section, aligned to
           the right, just left of the toggle section arrow"): the lock line sits on this
           collapse's HEADER row, as a sibling of the item (see step2.vue). -->
      <div v-if="isTowerCo()" class="approval-collapse" :class="{ 'approval-collapse--line': approvalLineShown(step) }">
      <!-- XOL-596 (approval lock): locked by whom, or "Not locked" with Lock. -->
      <approval-lock-line
        ref="lockLine"
        placement="header"
        :row="step"
        :title="step.stepName"
        :decided="approvalDecided(step)"
        :frozen="frozen"
        :can-lock="canLockApproval(step) && !readOnly && !frozen"
        :can-unlock="canUnlockApproval(step) && !readOnly && !frozen"
        :busy="lockRunning !== null"
        :busy-label="lockBusyLabel()"
        :format="formatDateTime"
        text-id="step3-approval-lock-line"
        @lock="lockApproval(step)"
        @unlock="unlockApproval(step)" />
      <el-collapse-item title="Finance Approval of Legal Document" name="FinApprove">

          <el-alert type="info" :title="'Approval of ' + (step3.docType ? step3.docType : 'legal documents')" show-icon :closable="false">
            <!-- XOL-596 (decision fields): the tick is a FIELD. It saves at the form's pause
                 like every other; the C-12 card carries Pending, Saving and Saved. -->
            <check-box v-model="step3.legalApproved" no-form :disabled="locked || frozen" @change="legalChecked">{{(step3.docType ? step3.docType : 'Legal Document')}}
              <template #cue><field-cue :row="actionData" path="state.step3.legalApproved" label="Finance Approval of Legal Document" layout="inline" dirty /></template>
            </check-box>
            <span v-if="step.approved"><b>{{formatDate(step.approved_at)}}</b> by <b>{{step.approved_by}}</b></span>
          </el-alert>

          <!-- XOL-596 (readable remarks): the heading was a closable `type="info1"` (an invalid
               type, so unstyled) el-alert shown only while the request was active. It is now a
               real heading, the same element above the editor and the read-only block. -->
          <section class="remarks-section">
            <h5 class="remarks-heading">Progress Remarks</h5>

            <!-- XOL-596 (completed steps, CS-R; approval lock, 2026-10-15): a read-only block with an
                 explicit empty state, for a completed (CS-R) or a locked approval. -->
            <!-- XOL-608: the editor renders only when it would be enabled. A viewer who cannot
                 write it (a request that went inactive while the drawer was open) gets the block,
                 never a disabled HtmlInput. -->
            <read-only-remarks v-if="financeRemarksReadOnly" aria-label="Progress Remarks" :html="step.remarks" />
            <template v-else>
              <html-input v-model="step.remarks" :disabled="isViewOnly() || !this.isActive(this.actionData)"
                @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="progressRemarksChanged" />
              <field-cue :row="step" path="remarks" label="Progress Remarks" layout="block" />
            </template>
          </section>

      </el-collapse-item>
      </div>

      <!-- XOL-626: Create Sharer -> Modify Sharer. The title and body follow `sharerMode`
           (the request's linked sharer, GET /licensees/action/{id}); the item keeps
           name="Sharer" in every state so the accordion's open state survives a title change. -->
      <el-collapse-item v-if="isTowerCo() && !disabled && step.actionType === 'RTS'" :title="sharerTitle" name="Sharer">

        <!-- UX §4.9: the OTHER form's save could not be re-read, so this one may be stale. -->
        <el-alert v-if="outOfDate.sharer && sharerMode === 'modify'" type="warning" :closable="false" show-icon
          title="This section may be out of date">
          <span>Your changes in Sharer Finance were saved, but this section couldn't be refreshed.</span>
          <el-button size="small" style="margin-top: 8px;" @click="retryOutOfDate('sharer')">Retry</el-button>
        </el-alert>

        <!-- loading: the lookup is running; nothing is mounted, so every child captures the
             loaded values in its own data() -->
        <div v-if="sharerMode === 'loading'" v-loading="true" element-loading-text="Loading sharer…"
          role="status" aria-label="Sharer" class="sharer-lookup-loading"></div>

        <!-- failed: no form on a failed lookup, ever -->
        <el-alert v-else-if="sharerMode === 'failed'" type="error" :closable="false" show-icon title="Couldn't load sharer">
          <span>This request's sharer could not be loaded, so it can't be viewed, created or modified yet.</span>
          <el-button size="small" style="margin-top: 8px;" @click="retrySharerLookup">Retry</el-button>
        </el-alert>

        <!-- modify: the sharer this request created -->
        <template v-else-if="sharerMode === 'modify'">
          <el-row>
            <h2 ref="sharerHeading" tabindex="-1" :id="'sharer-heading-' + actionData.id"><strong class="text-success">{{sharerReadOnly ? 'Linked' : 'Modify'}}</strong> Sharer</h2>
          </el-row>

          <p class="sharer-meta text-regular">
            <b v-if="linkedSharer.status" :class="sharerStatusClass"><i :class="sharerStatusIcon" aria-hidden="true"></i>&nbsp;{{linkedSharer.status}}</b>
            <span>Linked to this request.</span>
            <span v-if="linkedSharer.updated_at">Last update: <b>{{formatFriendlyDate(linkedSharer.updated_at)}}</b> ({{formatDateTime(linkedSharer.updated_at)}})<template v-if="linkedSharer.updated_by"> by {{linkedSharer.updated_by}}</template></span>
          </p>

          <!-- UX §5.4: a failed Create that found the sharer committed after all. Persistent, so not
               closable (design-system §9, XOL-626 D27); it clears on Edit and when the section flips. -->
          <el-alert v-if="recoveredLink" class="sharer-alert" type="info" show-icon :closable="false" title="A sharer was found for this request"
            description="Your Save didn't create a second one. The details below are what's saved, so check them and use Edit if anything needs changing." />

          <!-- XOL-605 / XOL-626 (D27): the sharer WAS created, but the API skipped some step-2 equipment
               rows (and says which). In context, not a $notify that stays until closed; it clears on
               Edit and on the next lookup that is not the Create flip's own. -->
          <el-alert v-if="assetWarnings.length" class="sharer-alert" type="warning" show-icon :closable="false" title="Some equipment wasn't added">
            <span class="sharer-conflict__line">The sharer was created, but these equipment rows from step 2 weren't added to the site's equipment:</span>
            <span v-for="(w, i) in assetWarnings.slice(0, 5)" :key="i" class="sharer-conflict__line">• {{ w }}</span>
            <span v-if="assetWarnings.length > 5" class="sharer-conflict__line">…and {{ assetWarnings.length - 5 }} more.</span>
            <span class="sharer-conflict__line">Add them on the site's Equipment tab.</span>
          </el-alert>

          <p v-if="sharerReadOnlyNote" class="text-regular" :id="'sharer-readonly-note-' + actionData.id"><i class="fa fa-lock" aria-hidden="true"></i> {{sharerReadOnlyNote}}</p>

          <div role="group" :aria-labelledby="'sharer-heading-' + actionData.id"
            :aria-describedby="sharerReadOnlyNote ? 'sharer-readonly-note-' + actionData.id : null">
            <licensee-form
              ref="sharerForm"
              :key="'lf-' + linkedSharer.id + '-' + sharerFormKey"
              :licensee="linkedSharer"
              :action-id="actionData.id"
              :read-only="sharerReadOnly"
              :edit-blocked="sharerEditBlocked"
              :focus-edit-on-mount="focusSharerEdit"
              :action-step-id="step3ActionStepId"
              :site-id="rowData.site_id"
              :site="rowData.site"
              :from-rta="!!rowData.rta_id"
              :sharer-name="actionData.organisation.name"
              :sharer-id="actionData.organisation.id"
              @updated="onSharerUpdated"
              @relookup="onSharerRelookup"
              @editing-change="onEditingChange('sharer', $event)" />
          </div>
        </template>

        <!-- conflict: the telco already has a sharer at this site that this request did not
             create. Information, not a fault (never `warning`): under D4 this is most legacy RTSs. -->
        <div v-else-if="sharerMode === 'conflict'" ref="sharerConflict" tabindex="-1" class="sharer-conflict">
          <el-alert type="info" :closable="false" show-icon :title="conflictTitle">
            <span class="sharer-conflict__line">{{conflictLine1}}</span>
            <span v-if="conflictLine2" class="sharer-conflict__line">{{conflictLine2}}</span>
            <span class="sharer-conflict__line">{{conflictLine3}}</span>
            <span v-if="showLinkButton || showConflictSharers || showConflictFinance" class="sharer-conflict__line sharer-conflict__actions">
              <!-- XOL-626 (S20, D26): the only way an unlinked sharer is linked to this request, by the user's own action -->
              <el-button class="btn-icon-gap" v-if="showLinkButton" ref="linkButton" size="small" type="primary" plain icon="fa fa-link" :loading="linking" @click="onLinkSharer">Link to this request</el-button>
              <el-button class="btn-icon-gap" v-if="showConflictSharers" type="text" size="small" icon="fa fa-users" :disabled="linking" @click="openConflictSite('sharers')">Open site Sharers</el-button>
              <el-button class="btn-icon-gap" v-if="showConflictFinance" type="text" size="small" icon="fa fa-money-bill-alt" :disabled="linking" @click="openConflictSite('finance')">Open site Finance</el-button>
            </span>
          </el-alert>
        </div>

        <!-- XOL-596 (completed steps, CS-S): a sharer can't be created from a completed step.
             The collapse item stays, so the user can still find it, and says why. -->
        <p v-else-if="frozen" class="text-regular"><i class="fa fa-lock" aria-hidden="true"></i> {{sharerNote}}</p>

        <!-- create -->
        <template v-else>
          <el-row>
            <h2><strong class="text-success">Add</strong> New Sharer</h2>
          </el-row>

          <licensee-form
            ref="sharerForm"
            key="lf-new"
            :busy="sharerRefreshing"
            :action-id="actionData.id"
            :action-step-id="step3ActionStepId"
            :site-id="rowData.site_id"
            :site="rowData.site"
            :from-rta="!!rowData.rta_id"
            :sharer-name="actionData.organisation.name"
            :sharer-id="actionData.organisation.id"
            :sharer-site-id="step2p2.state.siteId"
            :service-type="step2p2.state.serviceType"
            :spectrum-band="step2p2.state.spectrumBand"
            :equipment="step2.equipment"
            @created="onSharerCreated"
            @create-failed="onSharerCreateFailed" />
        </template>
      </el-collapse-item>

      <!-- XOL-626 (D3): the finance half of the same sharer, in its own item: the agreement pane is
           long, each form keeps one Edit button, and who can edit what stays unambiguous. -->
      <el-collapse-item v-if="isTowerCo() && !disabled && step.actionType === 'RTS' && sharerMode === 'modify'" title="Sharer Finance" name="SharerFinance">

        <el-alert v-if="outOfDate.finance" type="warning" :closable="false" show-icon title="This section may be out of date">
          <span v-if="financeOutOfDateCause === 'cancel'">Some of your changes in Sharer Finance were saved, but this section couldn't be refreshed.</span>
          <span v-else>Your changes in Modify Sharer were saved, but this section couldn't be refreshed.</span>
          <el-button size="small" style="margin-top: 8px;" @click="retryOutOfDate('finance')">Retry</el-button>
        </el-alert>

        <p v-if="financeNote" class="text-regular" :id="'sharer-finance-note-' + actionData.id"><i class="fa fa-lock" aria-hidden="true"></i> {{financeNote}}</p>

        <!-- XOL-626 (S14, D14): "Share rate with telco". A request attribute, not a licensee column, so
             it has its own row: it saves at once (no Edit / Save), is open to whoever can create the
             sharer (D2), and takes no part in either form's dirty guard. Not rendered until the read has
             loaded (a disabled switch would show "Off", which may be false), on Conflict / Create, or when the
             API does not have the routes yet. A Terminated sharer keeps it, disabled with its reason (D23). -->
        <template v-if="rateShareRowShown">
          <el-form label-position="top" class="rate-share" :aria-busy="rateShareSaving ? 'true' : null" @submit.native.prevent>
            <el-form-item :for="rateReadState === 'loaded' ? 'rate-share-' + actionData.id : null" label="Share rate with telco">
              <el-switch v-if="rateReadState === 'loaded'" ref="rateShareSwitch" :id="'rate-share-' + actionData.id"
                :value="!!rateShareValue" :disabled="rateShareDisabled"
                :active-text="isDesktop ? 'Yes' : null" :inactive-text="isDesktop ? 'No' : null" active-color="#67C23A" inactive-color="#F56C6C"
                @change="onRateShareChange" />
              <span v-if="rateShareStatus" class="rate-share__status text-regular"><i class="fa fa-spinner fa-spin text-primary" aria-hidden="true"></i> {{ rateShareStatus }}</span>
              <div v-if="rateShareHelp" :id="'rate-share-help-' + actionData.id" class="rate-share__help text-regular">{{ rateShareHelp }}<el-button v-if="rateReadState === 'failed'" class="rate-share__retry" type="text" size="small" @click="loadRateRead(true)">Retry</el-button></div>
              <span class="rate-share__sr" role="status" aria-live="polite">{{ rateShareAnnounce }}</span>
            </el-form-item>
          </el-form>
          <hr>
        </template>

        <div role="group" aria-label="Sharer Finance"
          :aria-labelledby="linkedDecorated ? 'sharer-finance-heading-' + linkedSharer.id : null"
          :aria-describedby="financeNote ? 'sharer-finance-note-' + actionData.id : null"
          :aria-busy="financeSaving ? 'true' : null">

          <finance-sharer
            v-if="linkedDecorated"
            :key="'fs-' + linkedSharer.id + '-' + financeKey"
            embedded
            :sharer="linkedSharer"
            :site-data="null"
            :read-only="financeReadOnly"
            :action-step-id="step3ActionStepId"
            :edit-blocked="financeEditBlocked"
            :rate-read="rateReadState === 'loaded' ? rateRead : null"
            :rate-read-state="rateReadState"
            @saved="onFinanceSaved"
            @resync="onFinanceResync"
            @retry-rate-read="loadRateRead(true)"
            @saving-change="financeSaving = $event"
            @editing-change="onEditingChange('finance', $event)" />

          <!-- the decorated GET failed: the sharer form above stays usable -->
          <el-alert v-else-if="sharerLookupFailed" type="error" :closable="false" show-icon title="Couldn't load the sharer finance details">
            <span>The rate and fees for this request's sharer couldn't be loaded.</span>
            <el-button size="small" style="margin-top: 8px;" @click="loadLinkedSharer()">Retry</el-button>
          </el-alert>

          <div v-else v-loading="true" element-loading-text="Loading sharer finance…" role="status"
            aria-label="Sharer Finance" class="sharer-lookup-loading"></div>
        </div>
      </el-collapse-item>

    </el-collapse>

      <!-- XOL-608 (Q1 default): a finance-access Tower Co viewer who cannot write the step (`disabled`:
           `!canWriteStep() || isViewOnly() || readOnly`) has no Finance
           collapse (`v-if="!disabled"` above), so these remarks vanished. The same block every other
           Tower Co viewer already gets below, where the collapse would render. The collapse and its
           tick stay hidden. -->
      <read-only-remarks v-else label="Progress Remarks" :html="step.remarks" />

    </section>


    <section v-else>

      <div v-if="isTowerCo()">
          <!-- XOL-596 (readable remarks): always shown, with "No remarks" when empty (the
               field used to vanish with no label), framed like every other read-only remarks. -->
          <read-only-remarks label="Progress Remarks" :html="step.remarks" />
        </div>

        <!-- Carlos, 2026-10-15: a counterparty's Progress Remarks is its party-2 row's field, which is
             written only on an RTB; on another request type it is not part of the form. -->
        <div v-else>
          <!-- XOL-626 (S16, D14/D21): the rate the Tower Co chose to share with this telco. A quiet read:
               when the option is off, the read fails, or the sharer is Terminated, NOTHING renders (no
               heading, no placeholder, no message) and this step looks exactly as it always has. The
               rate that applies to the telco's position today is highlighted; the fee is not shown. -->
          <section v-if="telcoRate" class="remarks-section" :aria-labelledby="'sharer-rate-heading-' + actionData.id">
            <h5 class="remarks-heading" :id="'sharer-rate-heading-' + actionData.id">Sharer Rate</h5>
            <p class="sharer-rate-intro text-regular">{{ telcoIntroText() }}</p>
            <sharer-rate-card :rate="telcoRate.rate" :version="telcoRate.version" :position="telcoRate.position"
              applies-to-you />
          </section>

          <section v-if="stepRemarksInForm(step)" class="remarks-section">
            <h5 class="remarks-heading">Progress Remarks</h5>

            <!-- XOL-526 (D5, S5): was an undisabled html-input — ANY
                 non-Tower-Co viewer (telco or provider) saw a live editor
                 with no gate at all, though a save had nowhere to go (PUT
                 /step was role:towerco pre-API-5). canWriteOwnStep() now
                 offers a real write, folding in the party-1-step-completed
                 lock (D9/A10). -->
            <!-- XOL-596 (completed steps, CS-R): a read-only block for every viewer. -->
            <!-- XOL-608: also for a viewer who cannot write it (view-only, a party that is not the
                 owner or provider, a read-only drawer): the editor renders only when it would be
                 enabled. -->
            <read-only-remarks v-if="progressRemarksReadOnly" aria-label="Progress Remarks" :html="step.remarks"
              :stamp="progressRemarksStamp ? lastUpdatedLine(progressRemarksStamp) : null" />
            <template v-else>
              <small v-if="progressRemarksNote" class="text-muted">{{progressRemarksNote}}</small>
              <html-input v-model="step.remarks" :disabled="!canWriteOwnStep(step) || readOnly" no-form
                @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="fieldChanged(step, 'remarks')" />
              <field-cue :row="step" path="remarks" label="Progress Remarks" layout="block" />
              <small v-if="progressRemarksStamp" class="remarks-stamp">{{lastUpdatedLine(progressRemarksStamp)}}</small>
            </template>

          </section>
        </div>

    </section>

  </div>

</template>

<script>

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

import LicenseeForm from '@/components/requests/LicenseeForm'
import FinanceSharer from '@/components/sites/finance/sharer/FinanceSharer'
import SharerRateCard from '@/components/sites/finance/sharer/SharerRateCard'

import BackhaulAction from '@/components/actions/BackhaulAction'
import BackhaulCustomer from '@/components/backhaul/BackhaulCustomer'
import BackhaulProvider from '@/components/backhaul/BackhaulProvider'

import { findBackhaulTwinId } from '@/lib/backhaulTwin'

import ApprovalRemarks from '@/components/requests/workflow/ApprovalRemarks'
import FieldCue from '@/components/requests/FieldCue'
import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import axios from '@/lib/axiosClient'
import { copyText } from '@/lib/requestSyncCopy'
import { revealDrawer } from '@/lib/layerNav'
import {
  ALREADY_LINKED,
  LINKED_ELSEWHERE,
  NOT_LINKED,
  SHARER_NOT_ACTIVE,
  SHARER_TERMINATED,
  errorCode,
  errorMessage,
  isRouteMissing,
  linkBody,
  linkPath,
  linkedSharerPath,
  normaliseRateRead,
  rateReadPath,
  rateShareBody,
  rateShareWritePath
} from '@/lib/requestSharer'

// XOL-652: a sharer status compared case-insensitively, as the API compares it (a lowercase
// "terminated" row must still read and behave as Terminated).
function statusIs (status, name) {
  return String(status || '').trim().toLowerCase() === name
}

// A copy of `obj` with one key set (an object the template watches is replaced, never patched).
function withKey (obj, key, value) {
  var copy = Object.assign({}, obj)

  copy[key] = value

  return copy
}

// XOL-626: the Terminated note, shared by the Modify Sharer and Sharer Finance items.
var TERMINATED_NOTE = "This sharer is terminated, so it can't be modified from this request. Its status is managed on Site → Finance → Sharers."
var FINANCE_NOT_MANAGER_NOTE = 'Only a finance, project, management or admin manager, or a super user, can change the rate and fees. You can still change the sharer details in Modify Sharer.'

export default {

  // XOL-534 (reviewer): DateMixins already arrives transitively via
  // RequestMixins -> SiteMixins — a direct import here was redundant.
  mixins: [RequestMixins],

  components: {
    ApprovalLockLine,
    ApprovalRemarks,
    BackhaulAction,
    BackhaulCustomer,
    BackhaulProvider,
    FieldCue,
    FileDownload,
    FileUpload,
    FinanceSharer,
    LicenseeForm,
    ReadOnlyRemarks,
    SharerRateCard
  },

  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    },

    isClosed: {
      type: Boolean,
      default: false
    }

  },



  methods: {

    // XOL-552: "+ Backhaul" saved a line — re-read the list. Deliberately does
    // NOT select the new row (the user picks it).
    onBackhaulLineAdded () {
      if (this.$refs.bhCustomer) {
        this.$refs.bhCustomer.refreshLines()
      }
    },

    // XOL-596: the fresh GET on open is adopted IN PLACE into the one object for
    // the row (F4), with the seq of when it was issued.
    loadOwnStep () {
      this.loadStep(this.stepData.id, this.step, this.afterStepLoad)
    },

    afterStepLoad (step) {
      // To handle existing RTx without this flag. (`legalApproved` is not a
      // column; it is a display flag on the row object.)
      step.legalApproved = (this.actionData.status !== 'Closed' ? step.legalApproved : true)
    },

    // -- XOL-626: the request's linked sharer ------------------------------------------------

    // Look up the sharer this request created (or the telco's unlinked one at the site).
    //  - 'loading' only when nothing has loaded yet: a re-read must never blank a mounted form,
    //    and a CREATE form that is only checking after a failed Save must keep the user's values
    //    (UX finding 2: key on `sharerLookup`, not on `linkedSharer`, which is null in Create).
    //  - the latest answer wins: a slow earlier read never overwrites a newer one.
    loadLinkedSharer (reason) {
      if (reason) {
        this.lookupReason = reason
      }

      if (this.sharerLookup !== 'loaded') {
        this.sharerLookup = 'loading'
      }

      this.sharerLookupFailed = false
      this.sharerRefreshing = true

      var seq = ++this.lookupSeq
      var self = this

      this.fetchData(
        linkedSharerPath(this.actionData.id),
        function (data) {
          if (seq === self.lookupSeq) self.onLinkedSharer(data)
        },
        'Sharer',
        function () {
          if (seq === self.lookupSeq) self.onLinkedSharerFailed()
        }
      )
    },

    retrySharerLookup () {
      this.sharerLookup = 'loading'
      this.loadLinkedSharer()
    },

    onLinkedSharer (data) {
      var reason = this.lookupReason
      var refresh = this.pendingRefresh

      this.lookupReason = null
      this.pendingRefresh = null

      this.linkedSharer = (data && data.licensee) || null
      this.existingSharer = (data && data.existing) || null
      this.linkedDecorated = !!(data && data.licensee)
      this.sharerLookup = 'loaded'
      this.sharerLookupFailed = false
      this.sharerRefreshing = false
      this.settleAssetWarnings()
      this.outOfDate = { sharer: false, finance: false }
      this.financeOutOfDateCause = null
      this.resyncFinance = false

      // XOL-626 (S14): the rate read follows the linked sharer. A sharer that is gone (Conflict, Create)
      // has no rate to read and no switch.
      if (this.linkedSharer) {
        this.loadRateRead()
      } else {
        this.resetRateRead()
      }

      // A save in one form re-reads the sharer so the OTHER form shows what was saved: remount
      // it now, or when it stops editing (an edit in progress is never discarded).
      if (refresh) {
        this.refreshForm(refresh)
      }

      if (reason === 'recover') {
        this.afterRecoveryLookup()
      } else if (reason === 'link-failed') {
        this.refocusAfterLinkFailure(true)
      } else if (reason === 'link-retried') {
        this.afterLinkRetryLookup(true)
      } else if (reason === 'link-reloaded' && this.linkedSharer) {
        // The Link whose answer was only {reloadFailed}: the success message is already up; the section
        // flips to Modify here, so Edit takes focus as it does after a whole answer.
        this.focusEditWhenMounted()
      }
    },

    // The skipped-equipment alert belongs to the Create flip: the lookup right after it keeps it, the
    // next one (a fresh read of the sharer) clears it.
    settleAssetWarnings () {
      if (this.assetWarningsHeld) {
        this.assetWarningsHeld = false
      } else {
        this.assetWarnings = []
      }
    },

    onLinkedSharerFailed () {
      var refresh = this.pendingRefresh
      var reason = this.lookupReason

      this.lookupReason = null
      this.pendingRefresh = null
      this.sharerRefreshing = false
      this.sharerLookupFailed = true

      // Only blank the section when nothing is mounted yet; fetchData already toasted.
      if (this.sharerLookup !== 'loaded') {
        this.sharerLookup = 'failed'
      }

      if (this.resyncFinance) {
        // Sharer Finance was Cancelled after a PARTIAL save and asked for the fresh row, which did not
        // come. It is the FINANCE section that is out of date (not Modify Sharer, which the Cancel never
        // touched). Remounting it from the last good read drops the values it kept after Cancel (the
        // failed write's) and clears its pending resync, so a later, unrelated re-read cannot rebuild it
        // under an edit in progress.
        this.resyncFinance = false
        this.financeOutOfDateCause = 'cancel'
        this.outOfDate = withKey(this.outOfDate, 'finance', true)
        this.remountForm('finance')
      } else if (refresh) {
        // UX §4.9: the save landed, the re-read did not: say the other section may be stale.
        this.outOfDate = withKey(this.outOfDate, refresh, true)
        this.financeOutOfDateCause = null
      }

      if (reason === 'recover') {
        this.afterRecoveryLookup()
      } else if (reason === 'link-failed') {
        this.refocusAfterLinkFailure(true)
      } else if (reason === 'link-retried') {
        this.afterLinkRetryLookup(false)
      }
    },

    // After a 409 already_linked that named the sharer just linked: the link landed if the lookup
    // shows this request linked to that sharer. Otherwise it's the ordinary "Not linked" refusal.
    afterLinkRetryLookup (lookedUp) {
      var linkedHere = lookedUp && this.linkedSharer &&
        Number(this.linkedSharer.id) === Number(this.linkingLicenseeId)

      if (linkedHere) {
        this.$message({ type: 'success', message: 'Sharer linked to this request.' })
        this.focusEditWhenMounted()
      } else {
        this.$notify({ title: 'Not linked', message: this.linkRetryMessage, type: 'warning', duration: 5500 })
        this.refocusAfterLinkFailure(true)
      }

      this.linkRetryMessage = null
    },

    // A form is remounted from the freshly read row, unless it is being edited right now.
    refreshForm (form) {
      if (this.editingForm[form]) {
        this.staleForm = withKey(this.staleForm, form, true)
        return
      }

      this.remountForm(form)
    },

    remountForm (form) {
      if (form === 'sharer') {
        this.sharerFormKey++
      } else {
        this.financeKey++
        // a remounted form announces no end to a save it was in the middle of
        this.financeSaving = false
      }
    },

    // Create succeeded: flip to Modify at once from the bare create response (no decorated
    // fields yet), then fetch the decorated row; the finance form mounts when it lands.
    onSharerCreated (lic) {
      var copy = Object.assign({}, lic)
      var warnings = copy.asset_warnings
      delete copy.asset_warnings

      // XOL-626 (D27): kept (the alert under the Modify meta line). The lookup that follows this flip is
      // the flip's own, so it must not clear them; any later one does (see onLinkedSharer).
      this.assetWarnings = Array.isArray(warnings) ? warnings.map(String) : []
      this.assetWarningsHeld = this.assetWarnings.length > 0

      this.linkedSharer = copy
      this.existingSharer = null
      this.linkedDecorated = false
      this.sharerLookup = 'loaded'
      this.recoveredLink = false
      this.focusEditWhenMounted()

      this.loadLinkedSharer()
    },

    // A failed Create may still have committed (a network error after the commit, a second tab,
    // 409 already_linked): look again. A linked sharer flips this section to Modify with a
    // recovery notice; an unlinked one at the site to Conflict; otherwise the Create form stays.
    onSharerCreateFailed () {
      this.loadLinkedSharer('recover')
    },

    // The Modify form's neutral "look again" (a Terminated sharer, a link that is gone).
    onSharerRelookup () {
      this.loadLinkedSharer('relookup')
    },

    // The sharer form that is about to mount (after the flip, or a recovery) takes focus on its
    // Edit button. The flag is read once, at mount, and cleared again in the next tick so a later
    // remount (a refresh after the finance form saved) never pulls focus out of that form.
    focusEditWhenMounted () {
      var self = this

      this.focusSharerEdit = true
      this.$nextTick(function () { self.focusSharerEdit = false })
    },

    afterRecoveryLookup () {
      var self = this

      if (this.linkedSharer) {
        this.recoveredLink = true
        this.focusEditWhenMounted()
        return
      }

      this.$nextTick(function () {
        if (self.existingSharer) {
          if (self.$refs.sharerConflict && self.$refs.sharerConflict.focus) self.$refs.sharerConflict.focus()
        } else if (self.$refs.sharerForm && self.$refs.sharerForm.focusSave) {
          self.$refs.sharerForm.focusSave()
        }
      })
    },

    onSharerUpdated (lic) {
      // The meta line's "Last update" is read from this object. A `reloadFailed` body ({id, action_id})
      // is not a row (the save committed, the API could not rebuild it): it stamps nothing, and the
      // re-read below supplies the real row.
      if (!(lic && lic.reloadFailed)) this.applyAuditStamp(this.linkedSharer, lic)

      // The saved values are now the form's own baselines; the stale flag (defence only: UX
      // §4.7 makes two editors at once unreachable) is cleared without remounting.
      this.staleForm = withKey(this.staleForm, 'sharer', false)

      this.refreshAfterSave('sharer')
    },

    // Fires once per write of a Save (override, bulk PUT, re-pin), so it may fire twice.
    onFinanceSaved () {
      this.staleForm = withKey(this.staleForm, 'finance', false)

      this.refreshAfterSave('finance')
    },

    // Sharer Finance was Cancelled after a PARTIAL save (some writes landed, one failed): it asks for the
    // fresh row and rebuilds from it (its `sharer` watcher). Modify Sharer is remounted from the same read.
    onFinanceResync () {
      this.staleForm = withKey(this.staleForm, 'finance', false)
      this.pendingRefresh = 'sharer'
      this.resyncFinance = true

      this.loadLinkedSharer()
    },

    // The "may be out of date" alerts' Retry: re-read, then remount the form the alert is about.
    retryOutOfDate (form) {
      this.pendingRefresh = form

      this.loadLinkedSharer()
    },

    // The saving form is NOT remounted (it advanced its own baselines, and FinanceSharer's
    // transient "Fees are recalculating" note would be lost); the OTHER one is.
    refreshAfterSave (source) {
      this.pendingRefresh = source === 'sharer' ? 'finance' : 'sharer'

      this.loadLinkedSharer()
    },

    onEditingChange (form, on) {
      this.editingForm = withKey(this.editingForm, form, !!on)

      // The recovery notice and the skipped-equipment alert clear on Edit.
      if (on && form === 'sharer') {
        this.recoveredLink = false
        this.assetWarnings = []
      }

      // Cancel (or the end of an edit) of a form that went stale: remount it from the fresh row.
      if (!on && this.staleForm[form]) {
        this.staleForm = withKey(this.staleForm, form, false)
        this.remountForm(form)
      }
    },

    // -- XOL-626 (S20, D26): link the telco's unlinked sharer to this request -------------------

    // The button asks first; nothing is sent until the user confirms. Esc and Cancel both reject
    // (XOL-629), and put focus back on the button.
    onLinkSharer () {
      if (this.linking || !this.showLinkButton) return

      var self = this
      var existing = this.existingSharer
      var asked = this.$confirm(this.linkConfirmBody, 'Link this sharer to the request?', {
        confirmButtonText: 'Link sharer',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })

      if (!asked || !asked.then) return

      asked.then(function () {
        // The section may have changed while the box was open (a lookup landed): only the sharer
        // the user was shown is linked, and only while the button would still show.
        if (self._isDestroyed || !self.showLinkButton || self.existingSharer.id !== existing.id) return

        self.linkSharer(existing)
      }, function () {
        self.refocusAfterLinkFailure(false)
      })
    },

    linkSharer (existing) {
      var self = this
      var settled = false

      this.linking = true
      this.linkingLicenseeId = existing.id

      var done = function (fn) {
        return function (arg) {
          settled = true
          fn.call(self, arg)
        }
      }

      // notifyFailureCallback = true: a coded 409 reaches `fail` with no toast. showMessage = false: the
      // success message is ours.
      var sent = this.send(this, this.getEndpoint(linkPath(this.actionData.id)),
        linkBody(this.step3ActionStepId, existing.id), 'Sharer',
        done(this.onSharerLinked), done(this.onSharerLinkFailed), axios.put, false, 'Save', null, true)

      // A write nothing answered (a 2xx that is not 200) must not leave the button spinning: look again.
      var unanswered = function () {
        if (settled || self._isDestroyed) return

        self.linking = false
        self.loadLinkedSharer('relookup')
      }

      if (sent && sent.then) sent.then(unanswered, unanswered)
    },

    // 200: the GET licensees/action/{id} body. Applied as the lookup would, so the section flips to
    // Modify (read mode) with focus on Edit, and the rate read follows.
    onSharerLinked (data) {
      this.linking = false

      if (!data || !data.licensee) {
        // Not the contract's body: the server's truth is one read away.
        this.loadLinkedSharer('relookup')
        return
      }

      this.$message({ type: 'success', message: 'Sharer linked to this request.' })

      // `reloadFailed`: the link COMMITTED but the API could not rebuild the row, so the body is only
      // {licensee: {id, action_id}, existing: null}. Never apply it (it is not decorated: the finance form
      // would mount on it and Modify would show no values). The server's truth is one read away.
      if (data.reloadFailed) {
        this.recoveredLink = false
        this.loadLinkedSharer('link-reloaded')
        return
      }

      // This answer is newer than any lookup still in flight.
      this.lookupSeq++
      this.recoveredLink = false
      this.onLinkedSharer(data)
      this.focusEditWhenMounted()
    },

    onSharerLinkFailed (err) {
      this.linking = false

      // A completed step: the request drawer says why (the C-12 card), never a toast.
      if (this.stepCompletedRefusal(err, 'sharerLink', { actionStepId: this.step3ActionStepId })) {
        this.refocusAfterLinkFailure(false)
        return
      }

      var code = errorCode(err)

      // A retry of a Link that already landed: axios-retry re-sends the PUT after a network error that
      // came AFTER the commit, and the retry answers 409 already_linked naming this very sharer. Say
      // nothing yet; look again, and confirm the link only if the lookup shows it (review suggestion).
      var body = err && err.response && err.response.data

      if (code === ALREADY_LINKED && body && Number(body.licensee_id) === Number(this.linkingLicenseeId)) {
        this.linkRetryMessage = errorMessage(err) || "This sharer couldn't be linked to this request."
        this.loadLinkedSharer('link-retried')
        return
      }

      if (code === ALREADY_LINKED || code === LINKED_ELSEWHERE || code === SHARER_NOT_ACTIVE) {
        this.$notify({
          title: 'Not linked',
          message: errorMessage(err) || "This sharer couldn't be linked to this request.",
          type: 'warning',
          duration: 5500
        })
        // The section re-renders from the server's truth.
        this.loadLinkedSharer('link-failed')
      }

      // every other failure: PostMixins' toast
      this.refocusAfterLinkFailure(false)
    },

    // Focus goes back to the Link button if it still renders, otherwise to the Conflict wrapper, and
    // (the sharer turned out to be linked here after all) the Modify heading. `onlyIfLost`: after a
    // lookup, never take focus from where the user has moved on to.
    refocusAfterLinkFailure (onlyIfLost) {
      var self = this

      this.$nextTick(function () {
        if (self._isDestroyed) return

        var active = typeof document !== 'undefined' ? document.activeElement : null

        if (onlyIfLost && active && active !== document.body) return

        var button = self.$refs.linkButton
        var target = (button && (button.$el || button)) || self.$refs.sharerConflict || self.$refs.sharerHeading

        if (target && target.focus) target.focus()
      })
    },

    // -- XOL-626 (S14): the rate read and the "Share rate with telco" switch ----------------

    // GET licensees/action/{id}/rate. QUIET (axios, never fetchData): no toast on any outcome.
    //   200               -> loaded
    //   bare 404          -> 'unavailable' (the API routes are not deployed): no switch, no cards
    //   404 not_linked    -> look the sharer up again, once
    //   anything else     -> 'failed' (a failed refresh of an already loaded read keeps what it has)
    // `userRetry` (the Retry buttons) re-arms the once-only re-lookup.
    loadRateRead (userRetry) {
      var id = this.actionData.id

      if (!id) return

      if (userRetry === true) this.rateReadRelooked = false
      if (this.rateReadState !== 'loaded') this.rateReadState = 'loading'

      var seq = ++this.rateReadSeq
      var self = this
      // A read issued (or landing) while the switch is being saved must not decide `shared`: the
      // write's answer wins, whichever order the two arrive in.
      var duringWrite = this.rateShareSaving

      axios.get(this.getEndpoint(rateReadPath(id))).then(function (resp) {
        if (seq !== self.rateReadSeq || self._isDestroyed) return

        var read = normaliseRateRead(resp && resp.data)

        if ((duringWrite || self.rateShareSaving) && self.rateRead) {
          read.shared = self.rateRead.shared
        }

        self.rateRead = read
        self.rateReadState = 'loaded'
        self.rateReadRelooked = false
      }).catch(function (err) {
        if (seq !== self.rateReadSeq || self._isDestroyed) return

        self.onRateReadFailed(err)
      })
    },

    onRateReadFailed (err) {
      if (isRouteMissing(err)) {
        this.rateRead = null
        this.rateReadState = 'unavailable'
        return
      }

      if (errorCode(err) === NOT_LINKED && !this.rateReadRelooked) {
        this.rateReadRelooked = true
        this.loadLinkedSharer('relookup')
        return
      }

      console.warn('Rate read failed', err)

      if (this.rateReadState !== 'loaded') {
        this.rateReadState = 'failed'
      }
    },

    resetRateRead () {
      this.rateReadSeq++
      this.rateRead = null
      this.rateReadState = 'idle'
      this.rateReadRelooked = false
      this.rateSharePending = null
      this.rateShareSaving = false
    },

    // The switch changed: save at once. The switch shows the PENDING value until the answer, a failure
    // puts it back, and a read that was issued before the write is dropped (the write's answer wins).
    onRateShareChange (v) {
      if (!this.rateRead || this.rateShareSaving || this.rateShareDisabled) return

      var self = this
      var settled = false

      this.rateSharePending = !!v
      this.rateShareSaving = true
      this.rateShareAnnounce = ''
      this.rateReadSeq++

      var done = function (fn) {
        return function (arg) {
          settled = true
          fn.call(self, arg)
        }
      }

      // notifyFailureCallback = true: a coded 409 (sharer_terminated) reaches `failed` with no toast.
      // showMessage = false: the success message is ours.
      var sent = this.send(this, this.getEndpoint(rateShareWritePath(this.actionData.id)),
        rateShareBody(v, this.step3ActionStepId), 'Rate sharing',
        done(this.onRateShareSaved), done(this.onRateShareFailed), axios.put, false, 'Save', null, true)

      // A write nothing answered (a 2xx that is not 200) must not leave the switch spinning.
      var unanswered = function () {
        if (settled || self._isDestroyed) return

        self.rateShareSaving = false
        self.rateSharePending = null
        self.loadRateRead()
      }

      if (sent && sent.then) sent.then(unanswered, unanswered)
    },

    onRateShareSaved (data) {
      // `reloadFailed`: the write COMMITTED but the API could not rebuild the §11.7 read, so the body is
      // only {shared}. Applying it would blank the rate, version and position cards: keep the previous
      // read with `shared` updated, then read again.
      var partial = !!(data && data.reloadFailed)
      var read = (partial && this.rateRead) ? Object.assign({}, this.rateRead) : normaliseRateRead(partial ? null : data)

      if (partial) {
        read.shared = (typeof data.shared === 'boolean') ? data.shared : !!this.rateSharePending
      } else if (!data || typeof data.shared !== 'boolean') {
        // The body is the §11.7 read; if `shared` is somehow missing, the value we sent is the answer.
        read.shared = !!this.rateSharePending
      }

      this.rateRead = read
      this.rateReadState = 'loaded'
      this.rateShareSaving = false
      this.rateSharePending = null

      this.$message({ type: 'success', message: read.shared ? 'Rate shared with the telco.' : 'Rate no longer shared with the telco.' })
      this.refocusRateShareSwitch()

      if (partial) this.loadRateRead()
    },

    onRateShareFailed (err) {
      var was = !!(this.rateRead && this.rateRead.shared)

      this.rateShareSaving = false
      this.rateSharePending = null
      this.rateShareAnnounce = "Rate sharing wasn't changed. It's still " + (was ? 'on.' : 'off.')
      this.refocusRateShareSwitch()

      // A completed step: the request drawer says why (the C-12 card), never a toast.
      if (this.stepCompletedRefusal(err, 'rateShare', { actionStepId: this.step3ActionStepId })) return

      var code = errorCode(err)

      if (code === SHARER_TERMINATED) {
        this.$notify({
          title: 'Not saved',
          message: errorMessage(err) || "This sharer has been terminated, so the rate sharing setting can't be changed from this request.",
          type: 'warning',
          duration: 5500
        })
        this.loadLinkedSharer('relookup')
        return
      }

      if (code === NOT_LINKED) {
        // PostMixins toasted the API's sentence: the link is gone, look again.
        this.loadLinkedSharer('relookup')
      }

      // every other failure: PostMixins' toast
    },

    // The switch is disabled while it saves, and the browser drops focus from a disabled input. Focus
    // "stays on the switch" (UX addendum 2 §1.4), so it is put back once the switch is enabled again,
    // unless the user has moved on to something else in the meantime.
    refocusRateShareSwitch () {
      var self = this

      this.$nextTick(function () {
        var sw = self.$refs.rateShareSwitch
        var input = sw && sw.$refs && sw.$refs.input
        var active = typeof document !== 'undefined' ? document.activeElement : null

        if (input && input.focus && (!active || active === document.body || active === input)) {
          input.focus()
        }
      })
    },

    // aria-describedby belongs on the native <input> inside Element's switch (its root div cannot take
    // focus, and attributes fall through to the root). This reaches an Element internal
    // (switch/src/component.vue); re-check it in the Vue 3 / Element Plus phase.
    describeRateShareSwitch () {
      var sw = this.$refs.rateShareSwitch
      var input = sw && sw.$refs && sw.$refs.input

      if (input && input.setAttribute) {
        input.setAttribute('aria-describedby', 'rate-share-help-' + this.actionData.id)
      }
    },

    // -- XOL-626 (S16, D14/D21/D22): the telco's view of the shared rate -------------------

    // Quiet. Two reads, and nothing renders unless BOTH say yes: the rate read (403 rate_not_shared when
    // the option is off) and the linked sharer's status (the rate read has no Terminated refusal, and
    // D22 never shows a Terminated sharer's rate). ANY failure leaves the step exactly as it was.
    loadTelcoRate () {
      var id = this.actionData.id
      var self = this

      axios.get(this.getEndpoint(rateReadPath(id))).then(function (resp) {
        var read = normaliseRateRead(resp && resp.data)

        if (!read.shared || !read.rate) return null

        return axios.get(self.getEndpoint(linkedSharerPath(id))).then(function (r2) {
          var lic = r2 && r2.data && r2.data.licensee

          if (!lic || statusIs(lic.status, 'terminated') || self._isDestroyed) return

          self.telcoRate = read
        })
      }).catch(function (err) {
        self.telcoRate = null
        console.warn('Telco rate view unavailable', err)
      })
    },

    // The intro of the telco card (UX addendum 2 §3, revised by D21: no fee sentence).
    telcoIntroText () {
      var read = this.telcoRate

      if (!read) return ''

      var parts = [this.towerCoName() + ' has shared the rate that applies to your sharer at this site.']
      var hasTiers = !!read.version && Array.isArray(read.version.tiers) && read.version.tiers.length > 0

      if (hasTiers) {
        var byQty = read.position && read.position.billingMode === 'Qty'

        parts.push('Each row is a period of the agreement, counted in months from the agreement start. ' + (byQty
          ? 'Each column is the monthly rate by the number of sharers on this site: #2 applies when there are two, #3 when there are three, and so on.'
          : 'Each column is the monthly rate for a sharer position at this site: #1 is the first sharer, #2 the second, and so on.'))

        if (read.position && read.position.applicablePosition !== null) {
          parts.push('The highlighted rate is the one that applies to you now.')
        }
      }

      return parts.join(' ')
    },

    // D15: open the site drawer so the user SEES it, even from a request drawer that is itself
    // stacked on a site drawer (see layerNav.revealDrawer).
    openConflictSite (which) {
      var args = { id: this.actionData.site_id, tab: which }

      if (which === 'finance') {
        args.subtab = 'sharers'
      }

      revealDrawer(this.$router, 'site', args)
    },

    // XOL-596 (decision fields, Carlos 2026-10-14): the legal-approval tick is a FIELD.
    // It marks the field changed and restarts the form's pause; at the pause ONE
    // approval command goes out, `POST /step/{id}/approval` with `expectedApproved` (a
    // boolean, contract C2) = the tick the user SAW (the field's base, never the
    // check-box, which has already flipped). Nothing is stamped locally (approved,
    // approved_at, approved_by): the server decides, mirrors it into
    // `state.step3.legalApproved`, and the adopt of the response shows it. Ticked and
    // unticked within the pause sends nothing.
    legalChecked () {
      // The section is Tower Co + finance only and the control `!disabled`; this is the
      // handler's own guard. A LOCKED approval is read-only.
      if (this.disabled || this.locked || this.frozen) {
        var rec = this.syncEnsureDecision(this.actionData)

        if (rec) {
          this.putBackDecision(rec, rec.decision.paths)
        }

        return false
      }

      return this.decisionChanged(this.actionData, 'state.step3.legalApproved')
    },

    // Agreement Type: a LOCKED or completed step 3 is read-only (2026-10-15). The select is
    // disabled, so this is the handler's own guard (defence in depth, like legalChecked):
    // the displayed value is put back and nothing is recorded. A lock that arrives AFTER a
    // change was recorded is the server's to refuse (403 approval_locked), at the save.
    docTypeChanged () {
      if (this.disabled || this.locked || this.frozen || !this.canAccess('finance')) {
        this.restoreServerValue(this.actionData, 'state.step3.docType')
        return false
      }

      return this.fieldChanged(this.actionData, 'state.step3.docType')
    },

    // Tower Co Progress Remarks (party-1 row): read-only while locked or completed. The
    // editor is unmounted then, so this is the same defence in depth.
    progressRemarksChanged () {
      if (this.locked || this.frozen) {
        this.restoreServerValue(this.step, 'remarks')
        return false
      }

      return this.fieldChanged(this.step, 'remarks')
    }

  },



  watch: {

    // The switch (and the native input inside it) is rendered once the read has loaded.
    rateReadState () {
      var self = this

      this.$nextTick(function () { self.describeRateShareSwitch() })
    },

    // The editors unmount when the step freezes, and an unmounting quill gets no blur: let go
    // of the text path they hold, so nothing stays held by focus.
    frozen (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // The editor unmounts when the approval locks (an adopt), and an unmounting quill gets no
    // blur: let go of the path it holds. Agreement Type is an `@change` select that is never
    // held, so it has nothing to release.
    locked (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // XOL-608: the editors also unmount when the viewer can no longer write them (the request
    // goes inactive, a counterparty stops being a writer), and an unmounting quill gets no blur.
    financeRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    progressRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    }

  },


  data () {

    return {
      accordion: 'FinApprove',
      actionData: this.rowData,
      site: this.rowData.site,
      step: this.stepData,
      step2: this.rowData.state.step2,
      step2p2: null,
      step3p1: null,
      step3: this.rowData.state.step3,

      // XOL-626: the sharer this request created, if any.
      linkedSharer: null,
      // The telco's sharer at this site when this request did not create it (a Conflict).
      existingSharer: null,
      sharerLookup: 'idle', // 'idle' | 'loading' | 'loaded' | 'failed'
      // True only when `linkedSharer` came from the GET (decorateSharer: calculatedRate,
      // overrideEnabled, rateName, rateVersion). The create response is the bare model, and
      // FinanceSharer must not mount on it.
      linkedDecorated: false,
      sharerLookupFailed: false,
      sharerRefreshing: false,
      // XOL-626 (S20): the Link call is in flight (the Link button spins, the site buttons are disabled).
      linking: false,
      linkingLicenseeId: null, // the sharer the last Link sent (S20)
      linkRetryMessage: null, // its 409 message, shown if the re-check doesn't confirm the link
      lookupSeq: 0,
      lookupReason: null, // 'recover' (a failed Create looked again) | 'relookup' | 'link-failed' | 'link-retried' | null
      pendingRefresh: null, // the form to remount when the in-flight read lands
      sharerFormKey: 0,
      financeKey: 0,
      editingForm: { sharer: false, finance: false },
      staleForm: { sharer: false, finance: false },
      outOfDate: { sharer: false, finance: false },
      recoveredLink: false,
      // XOL-626 (D27): the equipment rows the API skipped on Create (response.asset_warnings), and
      // whether the next lookup is the Create flip's own (it keeps them).
      assetWarnings: [],
      assetWarningsHeld: false,
      focusSharerEdit: false,
      // Sharer Finance is mid-save (several writes): aria-busy on its group.
      financeSaving: false,
      // Sharer Finance was Cancelled after a partial save and is waiting for the fresh row; why its
      // out-of-date alert is showing ('cancel' | null = the other form's save).
      resyncFinance: false,
      financeOutOfDateCause: null,

      // XOL-626 (S14): the request's rate read (the Tower Co's switch, the non-manager card).
      rateRead: null,
      rateReadState: 'idle', // 'idle' | 'loading' | 'loaded' | 'failed' | 'unavailable'
      rateReadSeq: 0,
      rateReadRelooked: false,
      rateSharePending: null, // the value being saved, shown until the answer
      rateShareSaving: false,
      rateShareAnnounce: '',
      // XOL-626 (S16): the rate shared with THIS telco, or null (the option is off, the read failed ...).
      telcoRate: null,

      types: [
        {
          value: 'LOO',
          label: 'LOO'
        }, {
          value: 'SLO',
          label: 'SLO'
        }, {
          value: 'AWO',
          label: 'AWO'
        }, {
          value: 'AAWO',
          label: 'AAWO'
        }, {
          value: 'AROW',
          label: 'AROW'
        }
      ]
    }

  }, // data


  computed: {

    // XOL-552: the Backhaul-category org that shares the RTB telco's name
    // (actionData.organisation is appended by the API), or null (e.g. DNB) — the
    // "+ Backhaul" Provider default. NOTE: getConfig() returns a module-level,
    // non-reactive object, so this computed caches the first value it computes
    // (it does not re-evaluate as the config hydrates). That is acceptable: the
    // config loads at boot and is cached in sessionStorage well before an RTB
    // can be opened.
    telcoBackhaulTwinId () {
      return findBackhaulTwinId(this.getConfig().orgGroups, this.actionData.organisation ? this.actionData.organisation.name : null)
    },

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-596 (completed steps): step 3 is complete, so everything in it is read-only for
    // every viewer until it is re-opened. A CONSTANT step number, from the server baseline.
    // NOT folded into `disabled`: the Finance collapse is `v-if="!disabled"` and must stay
    // rendered (the lock line lives on its header).
    frozen () {
      return this.isStepFrozen(3)
    },

    // The id of this request's step-3 party-1 row: the step context Create Sharer and the
    // backhaul forms send (`actionStepId`).
    step3ActionStepId () {
      var row = this.partyOneStepRow(3)

      return row ? row.id : null
    },

    sharerNote () {
      return copyText('requestSync.completed.sharer')
    },

    // XOL-626. First match wins: loading, failed, modify (a linked sharer), conflict (the
    // telco's unlinked sharer), else create. Derived in `computed`, never snapshotted in data().
    sharerMode () {
      if (this.sharerLookup === 'idle' || this.sharerLookup === 'loading') return 'loading'
      if (this.sharerLookup === 'failed') return 'failed'
      if (this.linkedSharer) return 'modify'
      if (this.existingSharer) return 'conflict'

      return 'create'
    },

    sharerTitle () {
      switch (this.sharerMode) {
        case 'modify': return 'Modify Sharer'
        case 'create': return 'Create Sharer'
        default: return 'Sharer'
      }
    },

    sharerTerminated () {
      return !!this.linkedSharer && statusIs(this.linkedSharer.status, 'terminated')
    },

    // -- XOL-626 (S14): the "Share rate with telco" row ---------------------------------------

    // Modify only (D20), and not where the API routes are missing. A Terminated sharer keeps the row, disabled (D23).
    rateShareRowShown () {
      return this.sharerMode === 'modify' && this.rateReadState !== 'unavailable'
    },

    rateShareValue () {
      return this.rateSharePending !== null ? this.rateSharePending : !!(this.rateRead && this.rateRead.shared)
    },

    // Frozen (step 3 complete), a Terminated sharer (D23), or a write in flight.
    rateShareDisabled () {
      return this.sharerReadOnly || this.rateShareSaving
    },

    rateShareStatus () {
      if (this.rateReadState === 'idle' || this.rateReadState === 'loading') return 'Loading…'
      if (this.rateShareSaving) return 'Saving…'

      return ''
    },

    // The first matching row of UX addendum 2 §1.3 (frozen first, then Terminated: D23), with the
    // D21 / D24 wording: the telco sees the rate and its tiers with the rate that applies to it highlighted, no fee.
    rateShareHelp () {
      if (this.rateReadState === 'idle' || this.rateReadState === 'loading') return ''
      if (this.rateReadState === 'failed') return "Couldn't load the rate sharing setting."

      var on = this.rateShareValue
      var state = on ? 'The telco can see this rate in its Step 3.' : "The telco can't see this rate."

      if (this.frozen) return state + " Step 3 is complete, so this can't be changed until the step is re-opened."

      if (this.sharerTerminated) return state + " This sharer is terminated, so this can't be changed from this request."

      if (!this.rateRead || !this.rateRead.rate) {
        return on
          ? 'No rate is assigned yet. The telco will see it in its Step 3 once one is assigned.'
          : 'No rate is assigned yet. Turn this on to show the telco the rate once one is assigned.'
      }

      return on
        ? 'The telco can see this rate and its tiers in its Step 3, with the rate that applies to its position highlighted. It never sees discounts, interest or whether the fee is overridden.'
        : "The telco can't see this rate. Turn this on to show it the rate and its tiers in its Step 3, with the rate that applies to its position highlighted."
    },

    // D2: no manager term. Whoever could create the sharer can modify it, unless the step is
    // complete or the sharer is Terminated (D5). A View-Only user never reaches the section.
    sharerReadOnly () {
      return this.frozen || this.sharerTerminated
    },

    financeReadOnly () {
      return this.sharerReadOnly
    },

    // The meta line's status: a word with an icon (design-system §5 / §14), not a tag.
    sharerStatusClass () {
      var status = this.linkedSharer ? this.linkedSharer.status : null

      if (statusIs(status, 'active')) return 'text-success'
      if (statusIs(status, 'terminated')) return 'text-danger'

      return 'text-info'
    },

    sharerStatusIcon () {
      var status = this.linkedSharer ? this.linkedSharer.status : null

      if (statusIs(status, 'active')) return 'fa fa-check-circle'
      if (statusIs(status, 'terminated')) return 'fa fa-times-circle'
      // XOL-652 D1: Expired is a known state (a grey hourglass), not "unknown" (a question mark).
      if (statusIs(status, 'expired')) return 'fa fa-hourglass-end'

      return 'fa fa-question-circle'
    },

    // One note, frozen first, then Terminated.
    sharerReadOnlyNote () {
      if (this.frozen) return copyText('requestSync.completed.sharerModify')
      if (this.sharerTerminated) return TERMINATED_NOTE

      return null
    },

    // Sharer Finance: the same two reasons, then the one only it has (UX §4.5).
    financeNote () {
      if (this.frozen) return copyText('requestSync.completed.sharerModify')
      if (this.sharerTerminated) return TERMINATED_NOTE
      if (!this.isManager('finance|project|management|admin')) return FINANCE_NOT_MANAGER_NOTE

      return null
    },

    // UX §4.7, one editor at a time: why the OTHER form's Edit is disabled. '' = blocked with no
    // hint (the re-read after a save is in flight, a fraction of a second).
    sharerEditBlocked () {
      if (this.sharerRefreshing) return ''
      if (this.editingForm.finance) return 'Save or cancel your changes in Sharer Finance first.'

      return null
    },

    financeEditBlocked () {
      if (this.sharerRefreshing) return ''
      if (this.editingForm.sharer) return 'Save or cancel your changes in Modify Sharer first.'

      return null
    },

    // -- Conflict (D4, D15) ----------------------------------------------------------------

    conflictTitle () {
      var org = this.actionData.organisation

      return (org && org.name ? org.name : 'This telco') + ' already has a sharer at this site'
    },

    conflictLine1 () {
      var existing = this.existingSharer

      if (existing && existing.action_id) {
        return "It was created from another request, so it can't be modified here."
      }

      // D26: offer the link only where the button is; where it is hidden (a completed step) the old
      // sentence is still the true one.
      return this.showLinkButton
        ? "It was added on the site page, or before requests kept a link to their sharer. If it's the sharer this request is for, link it to manage it here."
        : "It was added on the site page, or before requests kept a link to their sharer, so it can't be modified from this request."
    },

    // D26 / S20: "Link to this request". An UNLINKED sharer only (a linked one belongs to another
    // request), on an open step, for whoever may write it (the D2 gate). Nothing links by itself.
    showLinkButton () {
      var existing = this.existingSharer

      // D28 (Carlos, 2026-10-18): linking is manager-only, the PUT /licensees/{id} gate, so a
      // non-manager cannot take over a site-page sharer and then edit its billing dates through
      // the request PUT. The API refuses others with the role 403. isManager passes supers.
      return this.sharerMode === 'conflict' && !!existing && !existing.action_id &&
        !this.frozen && this.canWriteStep() && !this.isViewOnly() &&
        this.isManager('finance|admin|management|project|maintenance')
    },

    // Plain text (MessageBox escapes it). Missing parts are left out, as in line 2.
    linkConfirmBody () {
      return (this.conflictLine2 || 'This sharer') +
        ' will be linked to this request, and you can then modify it from Step 3. A sharer can only be linked to one request.'
    },

    // The available parts joined with ' · '; omitted when every part is missing.
    conflictLine2 () {
      var existing = this.existingSharer || {}
      var parts = []

      if (existing.sharerNo !== null && existing.sharerNo !== undefined) {
        parts.push(Number(existing.sharerNo) === 0 ? 'Sharer No. 0 (unassigned)' : 'Sharer No. ' + existing.sharerNo)
      }

      if (existing.status) parts.push(existing.status)
      if (existing.telcoSiteId) parts.push('Telco Site ID ' + existing.telcoSiteId)

      return parts.join(' · ')
    },

    // Each button hides exactly when the site's menu item for that tab would not render
    // (SiteDetails.vue:104 and :124), and both hide when the request carries no site id.
    showConflictSharers () {
      if (!this.actionData.site_id) return false

      var status = this.site ? this.site.status : undefined

      return this.isTowerCo() && this.canAccess('project|maintenance|finance|management') && !this.isLandOnly(status)
    },

    showConflictFinance () {
      if (!this.actionData.site_id) return false

      var status = this.site ? this.site.status : undefined

      return (this.isTowerCo() && !this.isSold(status)) || (this.canAccess('finance|management') && !this.isLandOnly(status))
    },

    conflictLine3 () {
      // With the Link button the site is the alternative ("Or ..."), not the way to change it.
      if (this.showLinkButton) {
        if (this.showConflictSharers && this.showConflictFinance) {
          return 'Or open the site: Sharers has the sharer details, Finance has the rate and fees.'
        }

        if (this.showConflictSharers) return "Or open the site's Sharers tab."
        if (this.showConflictFinance) return "Or open the site's Finance tab for the rate and fees."

        return 'Or open the site and edit the sharer there.'
      }

      if (this.showConflictSharers && this.showConflictFinance) {
        return 'To change it, open the site: Sharers has the sharer details, Finance has the rate and fees.'
      }

      if (this.showConflictSharers) return "To change it, open the site's Sharers tab."
      if (this.showConflictFinance) return "To change the rate and fees, open the site's Finance tab."

      return 'To change it, open the site and edit the sharer there.'
    },

    // XOL-596 (approval lock): step 3 locks the legal tick, Agreement Type and the Tower Co
    // Progress Remarks (2026-10-15, reversing Q-L1 / Q-L10); the counterparty's remarks
    // are not covered.
    locked () {
      return this.approvalLocked(this.step)
    },

    // XOL-526 (UX §2.4, U-I): Progress Remarks is the counterparty's own
    // party-2 STEP row — Tower Co's own row (party 1) is a different object
    // and is never shared, so it gets no note/stamp.
    progressRemarksStamp () {
      return (!this.isTowerCo() && this.step) ? this.editStamp(this.step.state, 'remarks') : null
    },

    progressRemarksNote () {
      return (this.canWriteOwnStep(this.step) && !this.isTowerCo() && !this.readOnly && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    },

    // XOL-608: the Finance collapse's Progress Remarks (Tower Co with finance access) shows the
    // block exactly when the editor's own `:disabled` would be true (`isViewOnly() ||
    // !isActive(actionData)`), OR'd with the complete / locked terms (XOL-596). A view-only
    // viewer never reaches it (the collapse is `v-if="!disabled"`); the term stays for parity.
    financeRemarksReadOnly () {
      return this.frozen || this.locked || this.isViewOnly() || !this.isActive(this.actionData)
    },

    // XOL-608: the counterparty's Progress Remarks (party-2 row).
    progressRemarksReadOnly () {
      return this.frozen || !this.canWriteOwnStep(this.step) || this.readOnly
    }

  },


  created () {

    if (this.step) {

      // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now write its
      // own party-2 row too.
      if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly) {
        this.loadOwnStep()
      }

      if (!this.readOnly) {

        // XOL-626: the lookup mirrors the collapse item's own gate (`disabled`), so a viewer
        // whose collapse is hidden makes no GET. (The dead `step.state.saved` flag, which
        // nothing ever wrote, is gone.)
        if (this.isTowerCo() && this.canAccess('finance') && this.step.actionType === 'RTS' &&
            this.canWriteStep() && !this.isViewOnly()) {
          this.loadLinkedSharer()
        }

        // XOL-626 (S16): the telco's own view of a shared rate. RTS, active (a Closed / Rejected one
        // mounts read-only through CompletedRequest), the telco that owns the request; a view-only
        // telco still reads. Never a Tower Co viewer, an RTB provider or another request type.
        if (!this.isTowerCo() && this.isRequestOwner() && this.actionData.actionType === 'RTS' &&
            this.isActive(this.actionData)) {
          this.loadTelcoRate()
        }

        this.step2p2 = this.findWorkflowStep(2, 2, this.rowData.steps)

        if (this.step && this.step.stepNo === 3 && this.step.partyNo === 1) {
          this.step3p1 = this.step
        } else {
          this.step3p1 = this.findWorkflowStep(3, 1, this.rowData.steps)
        }

        this.fixExistingEquipment() // just in case.

      }

    }

  },



  beforeDestroy () {

    // XOL-596: no save here. This used to send the whole step AND the whole
    // action (`saveStep(step, null, actionData, true)`) from whatever copy the
    // drawer held. The fields of this step save at the form's pause or at a flush
    // point; a text field still focused gets no blur on unmount.
    this.releaseFocused()

    if (!this.readOnly && this.step) {
      this.$emit('updated', this.step)
    }
  }

}
</script>

<style scoped>
/* XOL-626: the lookup placeholder (mirrors BackhaulCustomer's .bh-record-loading). */
.sharer-lookup-loading {
  min-height: 120px;
  margin-bottom: 1em;
}

/* The status word, "Linked to this request." and the last-update stamp, wrapping on a phone. */
.sharer-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin: 0 0 12px;
}

/* The in-context alerts under the Modify meta line (recovery, skipped equipment): Element's alert has no
   margin, so without this the next field's label touches it. */
.sharer-alert {
  margin: 0 0 12px;
}

/* Element wraps the alert's slot in a <p class="el-alert__description">, so the lines are spans. */
.sharer-conflict__line {
  display: block;
}

.sharer-conflict__actions {
  margin-top: 8px;
}

/* XOL-626 (S14): the "Share rate with telco" row. */
.rate-share__retry {
  margin-left: 4px;
}

.rate-share__help {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  max-width: 560px;
}

.rate-share__status {
  margin-left: 12px;
  font-size: 12px;
}

/* Element's switch has no focus style (its native input is opacity: 0). */
.rate-share ::v-deep .el-switch__input:focus-visible ~ .el-switch__core {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #409EFF;
}

/* Visually hidden, announced politely (same rule as BackhaulCustomer's). */
.rate-share__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* XOL-626 (S16): the telco card's intro. */
.sharer-rate-intro {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.45;
  max-width: 640px;
}
</style>
